import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Search } from 'lucide-react';
import { OnboardingState } from '@/hooks/useOnboardingState';

interface OrganizationActionSelectorProps {
  state: OnboardingState;
  updateState: (updates: Partial<OnboardingState>) => void;
  handleOrganizationSelect: (organization: any) => void;
}

export const OrganizationActionSelector = ({
  state,
  updateState,
  handleOrganizationSelect
}: OrganizationActionSelectorProps) => {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Building2 className="mx-auto h-12 w-12 text-primary mb-4" />
        <h3 className="text-xl font-semibold mb-2">Was möchten Sie tun?</h3>
        <p className="text-muted-foreground">
          Treten Sie einem bestehenden Team bei oder erstellen Sie ein neues Team mit eigener Organisation.
        </p>
      </div>

      <div className="space-y-4">
        {/* Join existing organization */}
        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${
            state.selectedAction === 'join' ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onClick={() => updateState({ selectedAction: 'join' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Bestehendem Team beitreten</h4>
              <p className="text-sm text-muted-foreground">
                Suchen Sie nach einem Team und treten Sie als Teammitglied bei
              </p>
            </div>
            <Badge variant={state.selectedAction === 'join' ? 'secondary' : 'outline'}>
              Beitreten
            </Badge>
          </div>
        </Card>

        {/* Search for organizations */}
        {state.selectedAction === 'join' && (
          <div className="space-y-4 ml-4">
            {/* Featured Teams */}
            <div className="space-y-2">
              <Label>Featured Teams</Label>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={() => handleOrganizationSelect({
                    id: '405970e3-7174-4945-912b-bd77dc06429b',
                    name: 'Vodafone Team DE KA BW',
                    color: '#E60000',
                    type: 'vodafone',
                    teamId: '255c11bc-b20c-4f6e-b111-7dc47270426a',
                    isFeaturedTeam: true,
                    logo_url: 'https://puwkrtqzszjsjkxowvin.supabase.co/storage/v1/object/public/organization-logos/6d4b0622-bc6e-44f1-a729-e589ebf1677f-logo-1754365397279.png'
                  })}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors relative ${
                    state.selectedOrganization?.name === 'Vodafone Team DE KA BW'
                      ? 'border-primary bg-primary/10' 
                      : 'border-muted-foreground/20 bg-muted hover:bg-muted/80'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-foreground flex items-center gap-2">
                        Vodafone Team DE KA BW
                        <Badge variant="secondary" className="bg-accent/10 text-accent-foreground border-accent/20 text-xs">
                          ⭐ Featured
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Empfohlenes Team
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Search Section - Only show if no organization selected */}
            {!state.selectedOrganization && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Nach Organisation suchen..."
                    value={state.searchQuery}
                    onChange={(e) => updateState({ searchQuery: e.target.value })}
                    className="pl-10"
                  />
                  {state.isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>

                {/* Search Results */}
                {state.searchResults.length > 0 && (
                  <Card className="max-h-60 overflow-y-auto">
                    <CardContent className="p-2">
                      {state.searchResults.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => handleOrganizationSelect(org)}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            state.selectedOrganization?.id === org.id 
                              ? 'bg-primary/10 border border-primary' 
                              : 'hover:bg-muted'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{org.name}</div>
                              <div className="text-sm text-muted-foreground">
                                {org.type === 'vodafone' ? 'Vodafone Organisation' : 'Benutzerdefinierte Organisation'}
                              </div>
                            </div>
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: org.color || '#3B82F6' }}
                            />
                          </div>
                        </button>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Selected Organization */}
            {state.selectedOrganization && (
              <div className="space-y-3">
                <Card className="border-success bg-success/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{state.selectedOrganization.name}</div>
                        <div className="text-sm text-success">
                          ✓ Sie werden als Teammitglied beitreten
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateState({ 
                          selectedOrganization: null, 
                          searchQuery: '' 
                        })}
                        className="text-success hover:text-success/80"
                      >
                        Ändern
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Create new organization */}
        <Card 
          className={`p-4 cursor-pointer transition-all hover:shadow-md ${
            state.selectedAction === 'create' ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          onClick={() => updateState({ selectedAction: 'create' })}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Neues Team erstellen</h4>
              <p className="text-sm text-muted-foreground">
                Erstellen Sie ein neues Team mit eigener Organisation und werden Sie Teamleiter
              </p>
            </div>
            <Badge variant={state.selectedAction === 'create' ? 'secondary' : 'outline'}>
              Erstellen
            </Badge>
          </div>
        </Card>
      </div>
    </div>
  );
};